// Domo to Amazon Quick page content, from the "Domo sub-page" design
// mockup. Rendered below the hero by PlatformMigrationContent (see
// components/BiPlatformPage). Copy the shape of this file for the other
// platforms; every section is optional.
export const domoPage = {
  whyMove: {
    eyebrow: 'Why move',
    title: 'Why teams move from Domo to Amazon Quick',
    intro: 'The reasons we hear most from Domo customers running on AWS.',
    cards: [
      { title: 'Your data is already on AWS', description: 'Skip the extra copy into Domo. Quick reads Redshift, S3, and Athena directly, with one governed layer.' },
      { title: 'AI agents on the same data', description: 'Once dashboards are on Quick, Chat Agents, Flows, and Research work on the same governed datasets.' },
      { title: 'Spend that counts on AWS', description: 'Quick and the AWS services under it can draw down your existing AWS commitment.' },
      { title: 'Security in one place', description: 'Access runs through IAM, SSO, and row-level security instead of a separate permission model.' },
    ],
  },

  metadata: {
    // Anchor for the hero's "See how we use your metadata" button.
    id: 'metadata',
    eyebrow: 'Built on your Domo metadata',
    title: 'We read your Domo instance through its APIs',
    intro: 'Instead of weeks of interviews, we pull metadata straight from Domo. Discovery takes days, every decision traces back to real usage, and the same metadata drives conversion, testing, and training.',
    columns: ['What we pull', 'From', 'How we use it'],
    rows: [
      { what: 'DataSets, schemas, row counts, refresh schedules', from: 'DataSet API', use: 'Size the data landing in Amazon S3 or Redshift and plan refresh pipelines' },
      { what: 'Cards, pages, and Beast Modes', from: 'Card and page metadata', use: 'Convert calculations, rebuild visuals, and spot duplicate cards' },
      { what: 'Magic ETL and SQL DataFlows', from: 'DataFlow definitions', use: 'Map lineage end to end and rebuild as AWS Glue or Redshift SQL' },
      { what: 'PDP policies', from: 'DataSet PDP endpoints', use: 'Recreate the same row-level security on Amazon Quick' },
      { what: 'Users and groups', from: 'User and Group APIs', use: 'Map to SSO and IAM groups and plan training by role' },
      { what: 'Activity log', from: 'Activity Log API', use: 'Find the most-used and unused cards to rank waves and retire dead content' },
      { what: 'Connectors and accounts', from: 'Account metadata', use: 'Plan connectivity for every source system' },
    ],
    benefits: {
      eyebrow: 'What you get from it',
      items: [
        { title: 'Full inventory and lineage', description: 'Every card traced back through its DataFlows to the source system.' },
        { title: 'Complexity score per card', description: 'Beast Modes, joins, and PDP rules scored, so each wave is sized realistically.' },
        { title: 'Wave plan from real usage', description: 'Waves ranked by the activity log, not by who asks loudest.' },
        { title: 'UAT test cases, generated', description: 'Expected values pulled from Domo through the API and compared with Quick automatically.' },
      ],
      note: {
        lead: 'Read-only access.',
        text: 'Your Domo admin creates an API client with read-only scopes. Nothing in Domo changes until you retire it.',
      },
    },
  },

  process: {
    eyebrow: 'How a Domo migration runs',
    title: 'Automated and AI-led, wave by wave',
    intro: 'Steps 3 to 5 repeat for each wave, so your team reviews exceptions instead of testing every card.',
    steps: [
      { tag: 'Automated', title: 'Automated assessment', description: "Metadata pulled through Domo's APIs: cards, DataSets, DataFlows, Beast Modes, PDP, and usage." },
      { tag: 'AI-led', title: 'Wave planning', description: 'Cards and pages grouped into waves by usage, dependencies, and business unit. Unused cards are flagged to retire.' },
      { tag: 'Automated', title: 'Automated conversion', description: 'DataFlows rebuilt as AWS pipelines, Beast Modes converted to calculated fields, and cards rebuilt as visuals.' },
      { tag: 'AI-led', title: 'AI-led UAT and QA', description: 'Every migrated card compared with its Domo original. Your team signs off on exceptions only.' },
      { tag: 'Automated', title: 'Enablement and training', description: 'Training per wave, built from who uses which Domo pages today.' },
    ],
  },

  afterMigration: {
    eyebrow: 'After the migration',
    title: 'More than dashboards: AI on the same data',
    items: [
      { title: 'Ask the data', description: 'A Chat Agent answers questions on the same datasets your old Domo cards used, with sources.' },
      { title: 'Automate the follow-up', description: 'Quick Flows turn a threshold alert into a task, an email, or a ticket.' },
      { title: 'Research with context', description: 'Quick Research blends your internal numbers with outside sources in one report.' },
    ],
  },

  // Shared AWS funding band (site.funding).
  showFunding: true,
};
